import { logFeature } from "../../scriptsCore/js/logging.js";
import core from "@trinitymirrordigital/chameleon-core/src/js/chameleon-core.js";

const events = core.events;
const wlog = (...args) => logFeature("wraparound", ...args);

const Site = {
  DRAGONFLY: "dragonfly",
  CHAMELEON: "chameleon",
  IN_YOUR_AREA: "iya",
  TEAM_DOGS: "dogs",
  CHILL: "2chill",
};

let config, utilities;

const baseConfig = {
  adsTopPositionToWindow: {
    breakpoints: {
      tabletPortraitUpwards: 100,
      tabletLandscapeUpwards: 103,
      desktop: 113,
    },
  },
  breakpointGutters: {
    tabletPortraitUpwards: 16,
    tabletLandscapeUpwards: 16,
    desktop: 32,
    widescreen: 32,
    "widescreen-extra": 32,
  },
  lockAdsScrollPosition: {
    breakpoints: {
      tabletPortraitUpwards: 100,
      tabletLandscapeUpwards: 70,
      desktop: 80,
    },
  },
  topFrame: {
    additionalSpacing: {
      tabletPortraitUpwards: 16,
      tabletLandscapeUpwards: 16,
      desktop: 32,
    },
    adFrameNativeWidth: 1000,
    isRichMedia: false,
  },
};

const siteConfigChameleon = {
  selectors: {
    wraparoundBody: "body",
    topSlot: ".top-slot",
    contentArea: ".wraparound-body main",
    header: "header",
  },
  layout: {
    topAdAboveHeader: true,
  },
  site: Site.CHAMELEON,
  siteSpecificClassName: "wraparound-chameleon",
  scaleContent: true,
  allowBackgroundColour: true,
};

const state = {};

const maxScale = 0.7;

/**
 * Attach/remove locking
 * @function lockUnlock
 * @param data.adObj {object} The DOM object for the ad (left, right or top)
 * @param data.lockAdsScrollPosition {integer} After how many pixels scrolled should the ads be locked
 * @param data.lockAfterPixels {integer} Where to lock the ads, based on the current breakpoint
 * @param data.scrollTopCurrent {integer} The current scroll offset
 */
function lockUnlock(data) {
  if (data.adObj) {
    if (data.scrollTopCurrent >= data.lockAfterPixels) {
      data.adObj.classList.add("fixed");
      data.adObj.style.top = data.lockAdsScrollPosition + "px";
    } else {
      data.adObj.classList.remove("fixed");
      data.adObj.style.top = ""; // Revert to the default CSS provided pixel values
    }
  }
}

/**
 * Lock the left and right hand wraparound frames if the user has scrolled beyond the acceptable (position: absolute) threshold
 * @function lockUnlockSideFrames
 * @param {Object} e The event (this is passed, since the event listener passes the event object as its first parameter. This messes with the null breakpoint and contextObj arguments if not defined as an argument)
 * @param {string} breakpoint The current breakpoint
 * @param {object} contextObj The DOM element to check offsets against
 */
function lockUnlockSideFrames(e, contextObj) {
  const currentBreakpoint = utilities.getBreakpoint();
  const scrollTopCurrent = contextObj
    ? contextObj.scrollTop
    : (window.pageYOffset || document.scrollTop) - (document.clientTop || 0) ||
      0;
  const adsTopPositionToWindow =
    config.adsTopPositionToWindow.breakpoints[currentBreakpoint];
  const lockAdsScrollPosition =
    config.lockAdsScrollPosition.breakpoints[currentBreakpoint];
  const lockAfterPixels = adsTopPositionToWindow - lockAdsScrollPosition;
  const leftAdObj = document.getElementById("wraparound-ad-left");
  const rightAdObj = document.getElementById("wraparound-ad-right");

  // Attach/remove locking
  if (leftAdObj) {
    lockUnlock({
      adObj: leftAdObj,
      lockAdsScrollPosition: lockAdsScrollPosition,
      lockAfterPixels: lockAfterPixels,
      scrollTopCurrent: scrollTopCurrent,
    });
  }

  if (rightAdObj) {
    lockUnlock({
      adObj: rightAdObj,
      lockAdsScrollPosition: lockAdsScrollPosition,
      lockAfterPixels: lockAfterPixels,
      scrollTopCurrent: scrollTopCurrent,
    });
  }
}

/**
 * Resize the heights of the wraparound ad frames, to ensure they start in the correct position, and stretch (execpt for the top frame) to the bottom of the viewport
 * @function resizeFrameDomElement
 * @private
 * @param takeOverFrameObj {object} The DOM element representing the current wraparound ad frame
 * @param obj.data.clickUrl {string} The URL of the frame, when clicked
 * @param obj.data.height [number] The height of the frame (only provided for the top frame)
 */
function resizeFrameDomElement(takeoverFrameObj, obj, contextHeight) {
  let containerHeight = contextHeight || window.innerHeight;

  // Dynamically set the height of the top wraparound frame to that of the value provided
  if (obj.position === "top") {
    // Calculate a ratio if NOT a rich media top frame
    if (!config.topFrame.isRichMedia) {
      let topAdHeight;
      let topAdWidth;
      let topAdAspectRatio;

      // Set the aspect ratio of the #wraparound-ad-top element
      if (!takeoverFrameObj.hasAttribute("data-ratio")) {
        topAdHeight = obj.data.height;
        topAdWidth = config.topFrame.adFrameNativeWidth;

        topAdAspectRatio = ((topAdHeight / topAdWidth) * 100).toFixed(2);

        takeoverFrameObj.setAttribute("data-ratio", topAdAspectRatio);
      } else {
        topAdAspectRatio = parseFloat(
          takeoverFrameObj.getAttribute("data-ratio")
        );
      }

      topAdWidth = takeoverFrameObj.offsetWidth;
      topAdHeight = Math.round((topAdWidth / 100) * topAdAspectRatio);

      takeoverFrameObj.style.height = topAdHeight + "px";
    }
  } else {
    takeoverFrameObj.style.height = containerHeight + "px";
  }
}

/**
 * Update the space to appear above the <main> DOM element, based on whether a top wraparound ad frame is visible in the DOM
 * @function updateTakeoverTopElement
 * @private
 * @param {string} scale the scale factor applied on the main element; if empty defaults to 1
 */
function updateTakeoverTopElement(scale) {
  // No additional spacing is required for a Rich Media frame, since that sits in the relatively positioned <div class="top-slot"> element
  if (config.topFrame.isRichMedia) {
    return;
  }
  const topTakeoverAd = document.getElementById("wraparound-ad-top");
  const wraparoundBody = document.querySelector(
    config.selectors.wraparoundBody
  );

  if (topTakeoverAd && config.layout.topAdAboveHeader) {
    const contentArea = document.querySelector(config.selectors.contentArea);
    scale =
      scale || (contentArea && contentArea.getAttribute("data-scale")) || 1;
    const topTakeoverAdHeight = topTakeoverAd.offsetHeight;
    const realTopTakeoverAdHeight =
      topTakeoverAdHeight + topTakeoverAdHeight * (1 - scale);
    wraparoundBody.style.marginTop = realTopTakeoverAdHeight + "px";
    if (config.layout.useNegativeTop) {
      topTakeoverAd.style.top = `-${realTopTakeoverAdHeight}px`;
    }
  } else {
    wraparoundBody.style.marginTop = "0px";
  }
}

/**
 * Append the frame DOM elements to the page
 * @function createTakeoverFrame
 * @private
 * @param [obj.data.clickUrl] {string} The URL of the frame, when clicked
 * @param [obj.data.height] [number] The height of the frame (only provided for the top frame)
 * @param [obj.isLeaderboard] [boolean] Whether or not the top frame is to receive a billboard or not
 * @param obj.position {string} Either 'left' || 'top' || 'right'
 * @param {number} contextHeight The height of the scrollable element (window.innerHeight by default)
 */
function createTakeoverFrame(obj, contextHeight) {
  if (!obj) {
    return;
  }

  const headerObj = document.querySelector(config.selectors.header);
  const takeoverFrameObj = document.createElement("div");
  const containerHeight = contextHeight || window.innerHeight;

  wlog("[INFO] Adding #wrapround-ad-" + obj.position + " to the DOM");

  // Add an identifier to the body, which indicates that the top wraparound frame contains Rich Media
  // This changes the top margin required on the <body>
  if (obj.data.isRichMedia) {
    config.topFrame.isRichMedia = true;
    document.body.classList.add("wraparound-rich-media");
  }

  // Collapse top slot if RichMedia is false
  if (
    Object.prototype.hasOwnProperty.call(obj.data, "isRichMedia") &&
    obj.data.isRichMedia === false
  ) {
    document
      .querySelector(config.selectors.topSlot)
      .classList.add("collapse-slot", "gpt", config.siteSpecificClassName);
  }

  if (!headerObj) {
    return;
  }

  takeoverFrameObj.setAttribute("id", "wraparound-ad-" + obj.position);
  takeoverFrameObj.setAttribute("data-tm-viewability-wrapper", "");

  takeoverFrameObj.classList.add(config.siteSpecificClassName);

  // Set the background colour of the container
  takeoverFrameObj.style.backgroundColor =
    obj.data.backgroundColor || obj.data.backgroundHex;

  // For regular wraparound frames (NOT Rich Media)
  if (!config.topFrame.isRichMedia) {
    takeoverFrameObj.style.backgroundImage =
      'url("' + obj.data.backgroundImage + '")';

    // Create the inner anchor element, which will allow each takeover frame to be clickable
    const anchorObj = document.createElement("a");
    anchorObj.setAttribute("href", obj.data.clickUrl);
    anchorObj.setAttribute("target", "_blank");

    // Append the anchor to the takeover frame
    takeoverFrameObj.appendChild(anchorObj);

    // Append the takeover frame to the DOM (after <header>)
    headerObj.parentNode.insertBefore(takeoverFrameObj, headerObj.nextSibling);

    // Having attached the wraparound frame to the DOM, resize its height, as required
    resizeFrameDomElement(takeoverFrameObj, obj, containerHeight);
    if (obj.position === "top") {
      // Find out when background img is loaded and resize accordingly
      const img = document.createElement("img");
      img.onload = () => {
        requestAnimationFrame(() => updateTakeoverTopElement());
        // To make sure enough spacing before header, recalculate using "updateTakeoverTopElement" again after half a second
        setTimeout(updateTakeoverTopElement, 500);
      };
      img.src = obj.data.backgroundImage;
    }
  } else {
    if (obj.position !== "top") {
      takeoverFrameObj.style.backgroundImage =
        'url("' + obj.data.backgroundImage + '")';

      // Create the inner anchor element, which will allow each takeover frame to be clickable
      const anchorObj = document.createElement("a");
      anchorObj.setAttribute("href", obj.data.clickUrl);
      anchorObj.setAttribute("target", "_blank");

      takeoverFrameObj.appendChild(anchorObj);

      // Append the (left and right) takeover frames to the DOM (after <header>)
      headerObj.parentNode.insertBefore(
        takeoverFrameObj,
        headerObj.nextSibling
      );

      // Having attached the wraparound frame to the DOM, resize its height, as required
      resizeFrameDomElement(takeoverFrameObj, obj, containerHeight);
    } else {
      // If the wraparound contains Rich Media, set the hex code/background color of the container for the rich media advert (.top-slot)
      document.querySelector(".top-slot").style.backgroundColor =
        obj.data.backgroundColor || obj.data.backgroundHex;
      events.fire(document.body, "wraparound.richMediaAd.loaded");
    }
  }
}

function handleResizeStop() {
  refreshAdHeights();
  scaleContent();
  scaleAds();
}

function handleScrollStop() {
  refreshAdHeights();
}

function forcePageLayout() {
  if (!config.layout.forceLeftAlign) {
    return;
  }
  const contentArea = document.querySelector(config.selectors.contentArea);
  if (contentArea) {
    const classes = ["wraparound-content-area"];
    if ((config.site = Site.TEAM_DOGS)) {
      classes.push("wraparound-team-dogs");
    }
    contentArea.classList.add(...classes);
  }
}

/**
 * Refresh the heights of the left and right hand side wraparound ad frames, to match the new height of the viewport (post-resize)
 * @function refreshAdHeights
 * @private
 */
function refreshAdHeights() {
  resizeFrameDomElement(document.getElementById("wraparound-ad-top"), {
    position: "top",
  });
  resizeFrameDomElement(document.getElementById("wraparound-ad-left"), {
    position: "left",
  });
  resizeFrameDomElement(document.getElementById("wraparound-ad-right"), {
    position: "right",
  });

  // Update the space to appear above the <main> DOM element, based on whether a top wraparound ad frame is visible in the DOM
  updateTakeoverTopElement();
}

/**
 * Attach a CSS classname to the <body> and <main> elements, so that CSS specificity is possible
 * @function setWraparoundClassname
 * @private
 */
function setWraparoundClassname() {
  document
    .querySelector(config.selectors.wraparoundBody)
    .classList.add("wraparound-body", config.siteSpecificClassName);

  const main = document.querySelector("main");
  if (main) {
    main.classList.add("wraparound", config.siteSpecificClassName);
  }
}

/**
 * Set the page background to the default white color
 * @function restoreDefaultPageBackgroundColor
 * @private
 */
function restoreDefaultPageBackgroundColor() {
  document.body.style.backgroundColor = state.origBackgroundColour || "#ffffff";
}

/**
 * Set the page background, if a colour has been provided by DFP
 * @function setPageBackgroundColor
 * @private
 * @param {string} color The hex code of the colour to assign as the page background
 */
function setPageBackgroundColor(color) {
  state.origBackgroundColour = document.body.style.backgroundColor;
  document.body.style.backgroundColor = color;
}

/**
 * Perform all (repeatable) DOM injection logic
 * @function initialSetup
 * @private
 * @param data {Object} The config data for the wraparound
 */
function initialSetup(adData) {
  // Check that the necessary data elements have been passed
  if (!adData || !adData.slots) {
    wlog(
      '[ERROR] The data coming from the "wraparound.load" event is incorrect. Data:',
      adData
    );
    return;
  }

  // Base layout breakpoint doesn't receive a wraparound
  if (utilities.isMobileBreakpoint()) {
    wlog(
      "[ERROR] A wraparound ad format was being loaded for a mobile breakpoint. Wraparound not created"
    );
    return;
  }

  if (document.body.classList.contains("wraparound-body")) {
    // somehow the top slot got refreshed and we're loading a new wrapround on top of an already existing one
    reset();
  }

  // Attach a CSS classname to the <body> so that CSS specificity is possible
  setWraparoundClassname();
  const wraparoundBody = document.querySelector(
    config.selectors.wraparoundBody
  );
  state.origBodyMarginTop = wraparoundBody.style.marginTop;
  disableRefresh();

  if (
    adData.background &&
    adData.background.color &&
    utilities.isDesktopBreakpoint() &&
    config.allowBackgroundColour
  ) {
    // Set the page background, if a colour has been provided by DFP
    setPageBackgroundColor(adData.background.color);
  }

  createTakeoverFrame({
    data: adData.slots.top,
    position: "top",
  });

  createTakeoverFrame({
    data: adData.slots.left,
    position: "left",
  });

  createTakeoverFrame({
    data: adData.slots.right,
    position: "right",
  });
}

function disableRefresh() {
  const slot = document.querySelector("#div-gpt-ad-top-slot");
  if (slot) {
    slot.setAttribute("data-no-refresh", "");
  }
}

/**
 * What is the current maximum width a GPT ad can be
 * @function maximumGPTAvailableWidth
 * @private
 * @return {Object} gutter The singular gutter value for the current breakpoint
 *                  availableWidth The maximum width a GPT ad can be
 */
function maximumGPTAvailableWidth() {
  let availableWidth;
  if (
    utilities.isTabletLandscapeBreakpoint() ||
    utilities.isTabletPortraitBreakpoint()
  ) {
    availableWidth = document.body.clientWidth - 120;
  }

  if (utilities.isDesktopBreakpoint()) {
    availableWidth = 1000;
  }

  // Account for the breakpoints gutters, when calculating the maximum width the advert can be sized to
  let gutter = config.breakpointGutters[utilities.getBreakpoint()];
  availableWidth -= gutter * 2;

  return {
    availableWidth: availableWidth,
    gutter: gutter,
  };
}

/**
 * Re-size a GPT ad slot if it is too wide with a wraparound in the browser
 * @function scaleGPTAd
 * @private
 * @param {object} adElement The DOM element for the GPT ad slot
 * @param {number} widths.availableWidth The current maximum width a GPT ad can be
 * @param {number} widths.gutter The singular gutter value for the current breakpoint
 */
function scaleGPTAd(adElement) {
  const elementInner = adElement.querySelector("div");
  const gptWidth = elementInner?.offsetWidth || 1;
  const widths = maximumGPTAvailableWidth();
  let scaleWidth;

  let parentStyles = window.getComputedStyle(adElement.parentElement, null);
  if (parentStyles.getPropertyValue("width") >= gptWidth) {
    return;
  }

  // By default, the scaling alignment will be from the left-edge, outwards. This caters for tablet breakpoints, where there is only one side wraparound frame
  // For desktop/widescreen/widescreen-extra, there is a left and a right wraparound frame, so the transform origin should be from the centre, outwards
  let transformOrigin = "top left";

  if (utilities.isDesktopBreakpoint()) {
    transformOrigin = "top center";
  }

  if (gptWidth > widths.availableWidth) {
    scaleWidth = widths.availableWidth / gptWidth;

    elementInner.style.transform = "scale(" + scaleWidth + ")";
    elementInner.style.transformOrigin = transformOrigin;
  }
}

/**
 * Scale ads that are outside the main container
 *
 * @function scaleAds
 */
function scaleAds() {
  if (!config.scaleContent) {
    // we don't do that here
    return;
  }
  const topLevelAds = utilities.nodeListToArray(
    document.querySelectorAll(".wraparound-body > .gpt")
  );

  topLevelAds.forEach(function (el) {
    scaleGPTAd(el);
  });
}

/**
 * Scales the content area so that the left and right frames can fit into the viewport.
 * @function scaleContent
 */
function scaleContent() {
  if (!config.scaleContent) {
    // we don't do that here
    return;
  }
  const contentArea = document.querySelector(config.selectors.contentArea);
  const leftFrame = document.getElementById("wraparound-ad-left");
  const rightFrame = document.getElementById("wraparound-ad-right");
  const pageType = utilities.getPageType();
  const stickyTopslot = document.querySelector(".sticky-top-slot .top-slot");
  let pageWidth = document.body.clientWidth;
  let leftWidth = 0;
  let rightWidth = 0;

  contentArea.style.removeProperty("width");

  if (utilities.isDesktopBreakpoint()) {
    leftWidth = 330 + parseInt(leftFrame.getBoundingClientRect().left, 10) || 0;
    rightWidth =
      330 +
        parseInt(
          document.body.clientWidth - rightFrame.getBoundingClientRect().right,
          10
        ) || 0;

    pageWidth = pageWidth - leftWidth - rightWidth;
  } else {
    pageWidth = pageWidth - 120;
  }

  let scale = pageWidth / contentArea.offsetWidth;
  let scaleProp;
  let scaleValue;
  if (scale < 1) {
    scale = scale < maxScale ? maxScale : scale;

    if (pageType === "live-event" || pageType === "article") {
      contentArea.style.width = `${pageWidth}px`;
      scaleProp = "width";
      scaleValue = `${pageWidth}px`;
      if (stickyTopslot) {
        stickyTopslot.style.maxWidth = `${pageWidth}px`;
      }
    } else {
      scaleProp = "transform";
      scaleValue = `scale(${scale})`;
      contentArea.style.transform = `scale(${scale})`;
    }
    // persist scale to use on updateTakeoverTopElement
    contentArea.setAttribute("data-scale", scale);
  } else {
    contentArea.removeAttribute("data-scale");
  }

  // Update the space to appear above the <main> DOM element, based on whether a top wraparound ad frame is visible in the DOM
  updateTakeoverTopElement(scale);

  //Wraparound scale complete fire the event.
  events.fire(document.body, "wraparound.scale.complete", {
    scaleProp,
    scaleValue,
  });
}

/**
 * Remove and reset all aspects of wraparound ads
 * @function reset
 */
function reset() {
  wlog("[INFO] Removing and re-initializing wraparound");

  // Remove all previously defined events, so that duplication events are never created upon re-initialization of wraparound (on breakpointchange)
  window.removeEventListener("resize.stop", handleResizeStop);

  window.removeEventListener("scroll.stop", handleScrollStop);

  // Set the page background to the default white color
  if (config.allowBackgroundColour) {
    restoreDefaultPageBackgroundColor();
  }

  const bodyObj = document.querySelector(config.selectors.wraparoundBody);

  // Remove classname(s) from body
  bodyObj.classList.remove("wraparound-body");
  bodyObj.classList.remove("wraparound-rich-media");
  bodyObj.classList.remove("wraparound-iya");

  // Remove the background colour from the body, provided by DFP config
  bodyObj.style.removeProperty("background-color");

  bodyObj.style.marginTop = state.origBodyMarginTop || "0px";

  let mainObj = document.querySelector("main");
  if (mainObj) {
    mainObj.classList.remove("wraparound");
    mainObj.style.removeProperty("padding-top");
  }

  const topSlot = document.querySelector(config.selectors.topSlot);
  if (topSlot) {
    topSlot.classList.remove("collapse-slot");
  }

  // Remove the 3 wraparound <div> elements
  const leftAdFrame = document.getElementById("wraparound-ad-left");
  const topAdFrame = document.getElementById("wraparound-ad-top");
  const rightAdFrame = document.getElementById("wraparound-ad-right");

  if (leftAdFrame) {
    wlog("[INFO] Removing #wrapround-ad-left from the DOM");
    leftAdFrame.parentNode.removeChild(leftAdFrame);
  }

  if (topAdFrame) {
    wlog("[INFO] Removing #wrapround-ad-top from the DOM");
    topAdFrame.parentNode.removeChild(topAdFrame);
  }

  if (rightAdFrame) {
    wlog("[INFO] Removing #wrapround-ad-right from the DOM");
    rightAdFrame.parentNode.removeChild(rightAdFrame);
  }

  //Wraparound scale reset fire the event.
  events.fire(document.body, "wraparound.scale.reset");

  // notify that the wraparound has gone away
  events.fire(window, "wraparound.reset");
}

/**
 * Initialise functionality for Wraparound Creative
 * @function init
 * @param data {Object} The config data for the wraparound
 */
function start(adData) {
  const supportedProviders = ["TM", "RCH"];
  if (
    !adData ||
    !adData.slots ||
    !supportedProviders.includes(adData.provider)
  ) {
    wlog(
      '[ERROR] The data coming from the "wraparound.load" event is incorrect. Data:',
      adData
    );
    return;
  }
  wlog("[INFO] Wraparound initiated with data", adData);

  initialSetup(adData);

  // // secondary nav disappears
  events.listen(window, "scroll.stop", handleScrollStop);

  // // Perform refresh/reset/re-calculations upon a resize completing
  events.listen(window, "resize.stop", handleResizeStop);

  scaleContent();
  forcePageLayout();
}

/**
 * Initialise module for Wraparound Creative
 * @function init
 * @param data {Object} The config data for the wraparound
 */
function init(siteDependencies, siteConfig = siteConfigChameleon) {
  config = Object.assign({}, baseConfig, siteConfig);
  utilities = Object.assign({}, siteDependencies);
}

export default {
  createTakeoverFrame,
  init,
  lockUnlockSideFrames,
  reset,
  resizeFrameDomElement,
  setWraparoundClassname,
  start,
};
